export const getUserProjectPreferenceKey = (projectOrPreference = {}) => {
  const businessId =
    projectOrPreference.businessId ||
    projectOrPreference.business_id ||
    '';
  const projectId = projectOrPreference.projectId || projectOrPreference.id || '';

  if (!businessId && !projectId) {
    return '';
  }

  return `${businessId}#${projectId}`;
};

/**
 * How the project dashboard shows a project's sensor readings to the user
 * (the preference's `sensorDisplay`): on gauges, the default, or as digital
 * readouts.
 */
export const SENSOR_DISPLAY = {
  GAUGES: 'gauges',
  DIGITAL: 'digital',
};

/**
 * A preference's sensor display: gauges without a choice, or with one the app
 * does not know (a backend without the field leaves it out).
 */
export const getSensorDisplayOfPreference = (preference) =>
  preference?.sensorDisplay === SENSOR_DISPLAY.DIGITAL
    ? SENSOR_DISPLAY.DIGITAL
    : SENSOR_DISPLAY.GAUGES;

export const isProjectHiddenByPreferenceOrAncestor = (
  project,
  preferencesByKey = {},
  projectsById = {}
) => {
  if (!project?.id) {
    return false;
  }

  const visitedProjectIds = new Set();
  let currentProject = project;

  while (currentProject?.id && !visitedProjectIds.has(currentProject.id)) {
    visitedProjectIds.add(currentProject.id);

    const preference =
      preferencesByKey[getUserProjectPreferenceKey(currentProject)] || {};

    if (preference.hidden) {
      return true;
    }

    if (
      !currentProject.parent ||
      currentProject.parent === currentProject.id ||
      currentProject.businessTypeEnum === 'EMPLOYER'
    ) {
      break;
    }

    currentProject = projectsById[currentProject.parent];
  }

  return false;
};

export const isProjectDirectlyHiddenByPreference = (
  project,
  preferencesByKey = {}
) => {
  if (!project?.id) {
    return false;
  }

  const preference =
    preferencesByKey[getUserProjectPreferenceKey(project)] || {};

  return Boolean(preference.hidden);
};

export const sortProjectsByPreference = (
  projects = [],
  preferencesByKey = {}
) => {
  return [...projects].sort((a, b) => {
    const aPreference = preferencesByKey[getUserProjectPreferenceKey(a)] || {};
    const bPreference = preferencesByKey[getUserProjectPreferenceKey(b)] || {};

    const aFavorite = Boolean(aPreference.favorite);
    const bFavorite = Boolean(bPreference.favorite);

    if (aFavorite !== bFavorite) {
      return aFavorite ? -1 : 1;
    }

    if (aFavorite && bFavorite) {
      const aUpdatedAt = aPreference.updatedAt || aPreference.createdAt || '';
      const bUpdatedAt = bPreference.updatedAt || bPreference.createdAt || '';

      if (aUpdatedAt !== bUpdatedAt) {
        return bUpdatedAt.localeCompare(aUpdatedAt);
      }
    }

    const aName = a?.name || '';
    const bName = b?.name || '';
    const nameSort = aName.localeCompare(bName);

    if (nameSort !== 0) {
      return nameSort;
    }

    return (a?.projectKey || a?.key || '').localeCompare(
      b?.projectKey || b?.key || ''
    );
  });
};

export const splitProjectsByHiddenPreference = (
  projects = [],
  preferencesByKey = {},
  projectsById = {}
) => {
  const visibleProjects = [];
  const hiddenProjects = [];

  projects.forEach((project) => {
    const isDirectlyHidden = isProjectDirectlyHiddenByPreference(
      project,
      preferencesByKey
    );

    if (isDirectlyHidden) {
      hiddenProjects.push(project);
      return;
    }

    if (
      !isProjectHiddenByPreferenceOrAncestor(
        project,
        preferencesByKey,
        projectsById
      )
    ) {
      visibleProjects.push(project);
    }
  });

  return {
    visibleProjects,
    hiddenProjects,
  };
};
